/**
 * The console's write path.
 *
 * The console does not get to `PATCH /jobs/:id`, and apps/api/src/routes/jobs.ts says why in
 * its header: "the server can never simply UPDATE a synced row from a handler. All writes
 * funnel through the command applier, or the two paths diverge and the whole thing rots."
 * So the manager's assign, his reschedule and his cancel are commands on /sync/push, exactly
 * like the technician's — the applier does not know or care that this one came from a desk.
 *
 * What the console deliberately does NOT inherit from the field app is the outbox. It is
 * online-assumed. A failed push is shown to the manager and he retries it himself; queueing
 * his cancellation for later delivery would be a promise this app has no service worker to
 * keep, and a cancellation that silently lands twenty minutes late is the exact failure the
 * conflict tray exists to clean up after.
 *
 * The device identity is real, though, and it has to be: `applied_ops` is keyed by
 * `(company_id, op_id)` and indexed by `(company_id, device_id, seq)`, so this browser is a
 * device with a stable id and a monotonic counter like any other. `seq` is a counter and not
 * a clock, for the same reason as on the phone (SYNC.md Rule 2).
 */

import { uuidv7 } from "@fanni/shared/ids";
import type { CommandResult, RejectReason } from "@fanni/shared/commands";
import { api } from "./api.js";

const DEVICE_KEY = "fanni.console.device_id";
const SEQ_KEY = "fanni.console.seq";

/** One id per browser profile, minted once and kept. */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = uuidv7();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/**
 * Monotonic, persisted, never a timestamp. Two console tabs share one localStorage, so they
 * share one counter — which is what we want: they are one device.
 */
function nextSeq(): number {
  const n = Number(localStorage.getItem(SEQ_KEY) ?? "0") + 1;
  localStorage.setItem(SEQ_KEY, String(n));
  return n;
}

/** Arabic for each refusal the applier can return. A rejection is never shown as a raw code. */
const REJECT_AR: Record<RejectReason, string> = {
  state_conflict: "حالة المهمة على الخادم تغيّرت. حدّث الصفحة وأعد المحاولة.",
  validation_failed: "البيانات المرسلة غير صالحة.",
  not_authorized: "ليس لديك صلاحية لهذا الإجراء.",
  entity_missing: "العنصر غير موجود على الخادم.",
  already_written: "هذا الإجراء مُسجّل مسبقاً ولا يمكن تغييره.",
  scope_violation: "الطلب يحاول الوصول إلى بيانات شركة أخرى.",
};

export class CommandRejected extends Error {
  constructor(
    readonly reason: RejectReason,
    readonly detail?: string,
  ) {
    super(REJECT_AR[reason] ?? "رُفض الإجراء.");
    this.name = "CommandRejected";
  }
}

interface PushResponse {
  results: CommandResult[];
  server_now: string;
}

/**
 * Sends one command and insists on an answer.
 *
 * `rejected` becomes a thrown error rather than a returned value on purpose: a rejection that
 * a caller can forget to check is a rejection the manager never sees, and the applier only
 * ever rejects things he needs to know about.
 */
export async function pushCommand(
  type: string,
  entityId: string,
  payload: unknown,
): Promise<CommandResult> {
  const envelope = {
    op_id: uuidv7(),
    device_id: deviceId(),
    seq: nextSeq(),
    type,
    entity_id: entityId,
    payload,
    client_ts: new Date().toISOString(),
  };

  const out = await api<PushResponse>("/sync/push", {
    method: "POST",
    body: { commands: [envelope] },
  });

  const result = out.results[0];
  if (!result) throw new Error("push returned no result for a single-command batch");
  if (result.status === "rejected") throw new CommandRejected(result.reason, result.detail);
  return result;
}
